<script setup lang="ts">
import { Swiper } from 'swiper/vue';
import type { Swiper as SwiperClass } from 'swiper';
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import type { NavigationOptions } from 'swiper/types';
import { NuxtLink } from '#components';

const props = defineProps<{
  title: string;
  subTitle?: string;
  titleLink?: string;
  /* Обычный переход с перезагрузкой, для разделов, которых пока нет во фронте */
  titleLinkExternal?: boolean;
  borderColor: string;
  backgroundColor: string;
  textColor: string;
  arrowColor: string;
  breakpoints: boolean | object;
  isControlVisible: boolean;
}>();

const swiperRef = ref<{ swiper: SwiperClass } | null>(null);
const navigationOptions = ref<NavigationOptions>({
  prevEl: null,
  nextEl: null,
});
const prevEl = ref<HTMLElement | null>(null);
const nextEl = ref<HTMLElement | null>(null);

onMounted(() => {
  if (prevEl.value && nextEl.value) {
    navigationOptions.value = {
      prevEl: prevEl.value,
      nextEl: nextEl.value,
    };
  }

  watch(
    () => props.isControlVisible,
    (newValue) => {
      const swiper = swiperRef.value?.swiper;
      if (!swiper) return;

      if (!newValue) {
        swiper.navigation?.destroy();
      } else {
        const swiper = swiperRef.value?.swiper;
        if (swiper && props.isControlVisible && swiper.navigation) {
          nextTick(() => {
            if (prevEl.value && nextEl.value) {
              swiper.navigation.destroy();
              swiper.navigation.init();
              swiper.navigation.update();
            }
          });
        }
      }
    },
  );
});
</script>

<template>
  <div
    :class="$style.slider"
    :style="{ backgroundColor: props.backgroundColor }"
  >
    <div :class="$style.slider__header">
      <component
        :is="titleLink ? NuxtLink : 'div'"
        :to="titleLink"
        :external="titleLink ? titleLinkExternal : undefined"
        :class="[
          $style.slider__headerInfo,
          { [$style.slider__headerInfo_link!]: titleLink },
        ]"
      >
        <div v-if="subTitle" :class="$style.slider__headerInfoSubTitle">
          {{ props.subTitle }}
        </div>
        <div
          :class="$style.slider__headerInfoTitle"
          :style="{ color: props.textColor }"
        >
          {{ props.title }}
        </div>
      </component>

      <div v-show="isControlVisible" :class="[$style.slider__controls]">
        <div
          ref="prevEl"
          :class="$style.slider__controlsItem"
          :style="{ borderColor: props.borderColor }"
        >
          <Icon
            :class="$style.slider__controlsIcon"
            name="my-icon:i-arrow-left"
          />
        </div>
        <div
          ref="nextEl"
          :class="$style.slider__controlsItem"
          :style="{ borderColor: props.borderColor }"
        >
          <Icon
            :class="$style.slider__controlsIcon"
            name="my-icon:i-arrow-right"
          />
        </div>
      </div>
    </div>
    <Swiper
      ref="swiperRef"
      :observer="true"
      :observe-parents="true"
      :class="$style.slider__swiperContainer"
      :modules="[Navigation]"
      :grab-cursor="true"
      :loop="true"
      :navigation="navigationOptions"
      :style="{ borderColor: props.borderColor }"
      :slides-per-view="breakpoints ? 1 : 'auto'"
      :slides-per-group="1"
      :breakpoints="
        breakpoints
          ? {
              768: {
                slidesPerView: 2,
              },
              1380: {
                slidesPerView: 3,
              },
            }
          : {}
      "
    >
      <slot />
    </Swiper>
  </div>
</template>

<style module lang="scss">
.slider {
  &__header {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    height: 64px;
    padding-left: var(--space-2-5x);
    gap: 42px;
    justify-content: space-between;

    &Info {
      &_link {
        text-decoration: none;

        @media (hover: hover) {
          transition: opacity 0.2s;

          &:hover {
            opacity: 0.85;
          }
        }
      }

      &Title {
        @include text_h5;
        color: var(--color-white);
      }
      &SubTitle {
        @include text_caption1_caps;
        color: var(--color-gray-text);
      }
    }
  }

  &__controls {
    display: flex;
    flex-flow: row nowrap;

    path {
      fill: v-bind(arrowColor);
    }

    &Item {
      display: flex;
      width: 100px;
      height: 64px;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      border-left: 1px solid;
    }
  }

  &__swiperContainer {
    border-top: 1px solid;
    border-bottom: 1px solid;
  }
}

@media (max-width: 479px) {
  .slider {
    &__header {
      padding-left: var(--space-2x);
      gap: 0;
    }
  }
}
</style>
